import { FC, useContext, useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { GroupButton } from "../../../../components/GroupButton";
import TextValidator from "../../../../components/input/text-validator";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import { IHoliday } from "../../../models/categoryModels";
import useMultiLanguage from "../../../../../hook/useMultiLanguage";
import AppContext from "../../../../../AppContext";
import * as Yup from "yup";
import { addLeaveRegulations, updateLeaveRegulations } from "../../../services/leaveRegulationsServices";
import { RESPONSE_STATUS_CODE } from "../../../../constants/moduleConsts";
import { RangeDatePicker } from "../../../../components/input/RangeDatePicker";
import moment from "moment";
import { hasAuthority } from "../../../../utils/functionUtils";
import { PERMISSION_ABILITY, PERMISSIONS } from "../../../../../constants";
import { INIT_HOLIDAY } from "../../../constants/leaveRegulationsConsts";

interface IProps {
  open: boolean;
  handleClose: () => void;
  handleCloseAndSearch: () => void;
  holidayInfo: IHoliday;
  isView: boolean;
}

const HolidayForm: FC<IProps> = (props) => {
  const {
    open,
    handleClose,
    handleCloseAndSearch,
    holidayInfo,
    isView
  } = props;

  const { lang } = useMultiLanguage();
  const { setPageLoading } = useContext(AppContext);
  const [isEdit, setIsEdit] = useState<boolean>(!isView);

  useEffect(() => {
    holidayInfo?.id && formik.setValues({ ...formik.values, ...holidayInfo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holidayInfo]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(lang("VALIDATION.REQUIRE")),
    startDate: Yup.date().required(lang("VALIDATION.REQUIRE")).nullable(),
    endDate: Yup.date()
      .when("startDate", {
        is: (value: Date | null) => value,
        then: Yup.date()
          .min(
            Yup.ref("startDate"),
            lang("VALIDATION.MINDATE") +
            lang("HOLIDAY.TIME") + " " +
            lang("INPUT.DATE_START")
          )
          .nullable(),
      })
      .required(lang("VALIDATION.REQUIRE"))
      .nullable()
  });

  const handleSubmit = async (values: IHoliday) => {
    try {
      setPageLoading(true);
      const { data } = holidayInfo?.id
        ? await updateLeaveRegulations(holidayInfo.id, values)
        : await addLeaveRegulations(values);
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(
          holidayInfo.id
            ? lang("TOAST.EDIT.SUCCESS")
            : lang("TOAST.CREATE.SUCCESS")
        );
        handleCloseAndSearch();
      } else {
        toast.warning(data?.message);
      }
    } catch (err) {
      toast.error(lang("GENERAL.ERROR"));
    } finally {
      setPageLoading(false)
    }
  };

  const formik = useFormik({
    initialValues: INIT_HOLIDAY,
    validationSchema,
    onSubmit: handleSubmit
  });

  useEffect(() => {
    if (formik.values?.startDate && formik.values?.endDate) {
      const startDate = moment(formik.values?.startDate);
      const endDate = moment(formik.values?.endDate);
      formik.setFieldValue("maxDay", endDate.diff(startDate, 'days') < 0 ? 0 : (endDate.diff(startDate, 'days') + 1))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.startDate, formik.values?.endDate])

  return (
    <Modal
      show={open}
      onHide={handleClose}
      backdrop="static"
      size="lg"
      centered
      className="custom-modal"
    >
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {holidayInfo?.id ? lang("HOLIDAY.EDIT") : lang("HOLIDAY.ADD")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-4 relative spaces z-index-4">
            <Col sm={12} md={12} lg={6} xl={6}>
              <TextValidator
                isRequired
                name="name"
                lable={lang("HOLIDAY.NAME")}
                type="text"
                onChange={formik.handleChange}
                value={formik.values?.name}
                readOnly={!isEdit}
                errors={formik.errors?.name}
                touched={formik.touched?.name}
              />
            </Col>
            <Col sm={8} md={8} lg={6} xl={6}>
              <RangeDatePicker
                label={lang("HOLIDAY.TIME")}
                startDateName="startDate"
                endDateName="endDate"
                isView={!isEdit}
                isRequired
                handleChange={formik.handleChange}
                value={formik.values}
                setFieldValue={formik.setFieldValue}
                touch={formik.touched}
                errors={formik.errors}
              />
            </Col>
            <Col sm={4} md={4} lg={2} xl={2}>
              <TextValidator
                name="maxDay"
                lable={lang("HOLIDAY.NUMBER_DATE")}
                type="number"
                value={formik.values?.maxDay}
                readOnly={true}
              />
            </Col>
            <Col sm={12} md={12} lg={10} xl={10}>
              <TextValidator
                name="description"
                lable={lang("DESCRIPTION")}
                type="text"
                value={formik.values?.description}
                onChange={formik.handleChange}
                readOnly={!isEdit}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          {(isEdit ? (
            <GroupButton type="btn-save" />
          ) : (hasAuthority(PERMISSIONS["DANH_MUC.CHAM_CONG.NGHI_LE"], PERMISSION_ABILITY.UPDATE) &&
            <GroupButton type="btn-edit" handleSaveEdit={() => setIsEdit(true)} />
          ))
          }
          <GroupButton
            type="btn-cancel"
            handleCloseUpdateDialog={handleClose}
          />
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export { HolidayForm };